import { Expression } from 'mapbox-gl';
import { useMemo } from 'react';
import { FillLayer, Layer, LineLayer, MapRef, Source } from 'react-map-gl';
import { useAppSelector } from '../../../app/hooks';
import { Block } from '../../../services/entities/impl/blfApi/schemas/block';
import { LayerExistenceDict } from '../hooks/useCheckLayers';
import { LatestScanDict } from '../hooks/useFindLatestScans';
import useLayerOrder from '../hooks/useLayerOrder';
import { LayerEnum, selectLayerVisibility } from '../mapSlice';
import { ensureLayerExists } from '../mapUtils';
import { addMapBlockInfo } from '../utils/addMapBlockInfo';
import { EMPTY_FEATURE_COLLECTION } from '../utils/constants';

export enum LatestScanColors {
  oneWeek = '#0080bf',
  thirtyDays = '#00acdf',
  threeMonths = '#55d0ff',
  sixMonths = '#7ce8ff',
  oneYear = '#ccf9ff',
  greaterThanOneYear = '#D3D3D3',
  defaultColor = 'black',
}

const colorConfiguration: Expression = [
  'case',
  ['==', ['get', 'daysSinceNow'], null],
  LatestScanColors.defaultColor, // No Scans
  ['<=', ['get', 'daysSinceNow'], 7],
  LatestScanColors.oneWeek,
  ['<=', ['get', 'daysSinceNow'], 30],
  LatestScanColors.thirtyDays,
  ['<=', ['get', 'daysSinceNow'], 90],
  LatestScanColors.threeMonths,
  ['<=', ['get', 'daysSinceNow'], 182],
  LatestScanColors.sixMonths,
  ['<=', ['get', 'daysSinceNow'], 365],
  LatestScanColors.oneYear,
  ['>', ['get', 'daysSinceNow'], 365],
  LatestScanColors.greaterThanOneYear,
  LatestScanColors.defaultColor, // Default
];

interface Props {
  mapRef: MapRef | null;
  layerExistenceDict: LayerExistenceDict;
  blocks: Block[] | undefined;
  latestScans: LatestScanDict;
}

const BlockOutlineLayer: React.FC<Props> = ({
  mapRef,
  layerExistenceDict,
  blocks,
  latestScans,
}) => {
  const layerVisibility = useAppSelector(selectLayerVisibility);
  const layerBeforeId = useLayerOrder(
    LayerEnum.blockOutlines,
    layerExistenceDict
  );

  const blockFeaturesCollection = useMemo(() => {
    if (blocks) {
      return addMapBlockInfo(blocks, latestScans) || EMPTY_FEATURE_COLLECTION;
    }
    return EMPTY_FEATURE_COLLECTION;
  }, [blocks, latestScans]);

  const outlineLayerStyle = useMemo<LineLayer>(
    () => ({
      id: `${LayerEnum.blockOutlines}-line`,
      type: 'line',
      paint: {
        'line-color': colorConfiguration,
        'line-opacity': layerVisibility[LayerEnum.blockOutlines] ? 1 : 0,
        'line-width': 3,
      },
    }),
    [layerVisibility[LayerEnum.blockOutlines]]
  );

  const fillLayerStyle = useMemo<FillLayer>(
    () => ({
      id: `${LayerEnum.blockOutlines}`,
      type: 'fill',
      paint: {
        'fill-color': colorConfiguration,
        'fill-opacity': layerVisibility[LayerEnum.blockOutlines] ? 0.4 : 0,
      },
    }),
    [layerVisibility[LayerEnum.blockOutlines]]
  );

  return (
    <Source
      key={LayerEnum.blockOutlines}
      id={LayerEnum.blockOutlines}
      type='geojson'
      data={blockFeaturesCollection}
    >
      {outlineLayerStyle && (
        <Layer
          key={LayerEnum.blockOutlines}
          {...outlineLayerStyle}
          beforeId={ensureLayerExists(mapRef, layerBeforeId)}
        />
      )}
      {fillLayerStyle && (
        <Layer
          key={`${LayerEnum.blockOutlines}-line`}
          {...fillLayerStyle}
          beforeId={ensureLayerExists(mapRef, layerBeforeId)}
        />
      )}
    </Source>
  );
};

export default BlockOutlineLayer;
